import type { FC } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';

import { Filter, Search, Users } from 'lucide-react';

import { Button, Input, LoadingSpinner } from '@/components/ui';
import { LeadCountBadge, LeadsListTags, NO_TAGS_VALUE, TagFilterDropdown } from '@/features/smswiser-campaigns/components/shared';
import { cn } from '@/lib/utils';

import { SectionHeader } from '../form/SectionHeader';

interface LeadsColumnProps {
  tagColors?: Record<string, string>;
  /** Performance tags (High Profit, etc.) are still loading */
  tagsLoading?: boolean;
  /** Current tag filter: '' = all, tag name, or NO_TAGS_VALUE */
  leadTagFilter: string;
  setLeadTagFilter: (v: string) => void;
  /** All unique tags for filter dropdown */
  availableTags: string[];
  leadsLists: {
    id: number;
    name: string;
    count?: number | null;
    usageToday?: number;
    remaining?: number | null;
    performanceTags?: string[];
  }[];
  usageByListId?: Record<string, number>;
  leadSearch: string;
  setLeadSearch: (v: string) => void;
  filteredLeads: {
    id: number;
    name: string;
    count?: number | null;
    usageToday?: number;
    remaining?: number | null;
    performanceTags?: string[];
  }[];
  selectedLeadIds: number[];
  setSelectedLeadIds: (v: number[]) => void;
  selectedSet: Set<number>;
  recentLeadIds: number[];
  toggleLead: (id: number) => void;
}

export const LeadsColumn: FC<LeadsColumnProps> = ({
  tagColors,
  tagsLoading,
  leadTagFilter,
  setLeadTagFilter,
  availableTags,
  leadsLists,
  usageByListId,
  leadSearch,
  setLeadSearch,
  filteredLeads,
  selectedLeadIds,
  setSelectedLeadIds,
  selectedSet,
  recentLeadIds,
  toggleLead,
}) => {
  return (
    <div className="flex min-h-[300px] w-full min-w-0 shrink-0 flex-col overflow-hidden border-b border-border xl:w-[360px] xl:min-h-0 xl:border-b-0">
      <SectionHeader
        icon={Users}
        title="Leads Lists"
        subtitle="Select one or more lists"
        subtitleSlot={
          tagsLoading ? (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <LoadingSpinner size="xs" />
              Loading performance tags…
            </p>
          ) : undefined
        }
        actions={
          selectedLeadIds.length > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedLeadIds([])}
              className="h-7 text-xs text-destructive hover:bg-destructive/10"
            >
              Clear ({selectedLeadIds.length})
            </Button>
          ) : undefined
        }
      />

      <div className="min-w-0 border-b border-border/60 bg-background p-2.5">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <div className="relative min-w-0 flex-1 basis-full sm:basis-0">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={leadSearch}
              onChange={(e) => setLeadSearch(e.target.value)}
              placeholder="Search leads lists..."
              className="min-w-0 pl-9"
            />
          </div>
          {availableTags.length > 0 && (
            <div className="flex min-w-0 shrink items-center gap-2">
              <Filter className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <TagFilterDropdown
                value={leadTagFilter === '(none)' ? NO_TAGS_VALUE : leadTagFilter}
                options={availableTags}
                tagColors={tagColors}
                displayLabel={
                  leadTagFilter === ''
                    ? 'All tags'
                    : leadTagFilter === '(none)'
                      ? 'No tags'
                      : leadTagFilter
                }
                onSelect={(v) =>
                  setLeadTagFilter(v === '' ? '' : v === NO_TAGS_VALUE ? '(none)' : v)
                }
                placeholder="All tags"
                compact
              />
            </div>
          )}
        </div>

        {recentLeadIds.length > 0 ? (
          <div className="mt-2 flex min-w-0 flex-wrap gap-1.5 overflow-hidden">
            {recentLeadIds.slice(0, 6).map((id) => {
              const list = leadsLists.find((l) => l.id === id);
              if (!list) return null;
              const isSel = selectedLeadIds.includes(id);
              return (
                <button
                  key={id}
                  onClick={() => toggleLead(id)}
                  title={list.name}
                  className={cn(
                    'max-w-[160px] truncate rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors',
                    isSel
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-muted/20 text-muted-foreground hover:bg-muted/40',
                  )}
                >
                  {list.name}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Capped on xl too: the creator now sizes to its content, and an
       *  uncapped list of leads would decide that height for everyone —
       *  pushing the config fields back into a scrollbar. A long list scrolls
       *  here, which is what a list is for. */}
      <div className="leads-grid-container max-h-[46vh] min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-muted/5 p-2.5 xl:max-h-[62vh]">
        <div className="leads-grid w-full">
          {filteredLeads.map((lead) => {
            const isSelected = selectedSet.has(lead.id);
            return (
              <RadixTooltip.Provider key={lead.id} delayDuration={150} skipDelayDuration={80}>
                <RadixTooltip.Root>
                  <RadixTooltip.Trigger asChild>
                    <div
                      onClick={() => toggleLead(lead.id)}
                      className={cn(
                        'group relative flex min-w-0 cursor-pointer flex-col gap-1 rounded-md border p-2 transition-colors hover:border-primary/50',
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border/60 bg-card',
                      )}
                    >
                      <div className="flex min-w-0 items-start justify-between gap-2">
                        <span
                          className={cn(
                            'min-w-0 break-words text-xs font-medium leading-tight',
                            isSelected ? 'text-primary' : 'text-foreground',
                          )}
                          title={lead.name}
                        >
                          {lead.name}
                        </span>
                        <LeadCountBadge
                          count={lead.count}
                          usage={lead.usageToday ?? usageByListId?.[String(lead.id)]}
                          remaining={lead.remaining}
                          compact
                        />
                      </div>
                      {(lead.performanceTags?.length ?? 0) > 0 ? (
                        <LeadsListTags
                          performanceTags={lead.performanceTags}
                          tagColors={tagColors}
                        />
                      ) : null}
                    </div>
                  </RadixTooltip.Trigger>
                  <RadixTooltip.Portal>
                    <RadixTooltip.Content
                      side="top"
                      sideOffset={8}
                      avoidCollisions
                      collisionPadding={16}
                      className="z-[300] max-w-[min(400px,92vw)] rounded-lg border border-border bg-popover px-3 py-2.5 text-popover-foreground shadow-lg"
                    >
                      <div className="flex flex-col gap-2 text-left">
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">
                            List
                          </div>
                          <div className="break-words text-sm font-medium">{lead.name}</div>
                        </div>
                        {(lead.performanceTags?.length ?? 0) > 0 && (
                          <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">
                              Tags
                            </div>
                            <div className="mt-1 flex flex-wrap gap-1.5">
                              {(lead.performanceTags ?? []).map((tag) => (
                                <span
                                  key={tag}
                                  className="rounded px-2 py-0.5 text-[10px] font-medium"
                                  style={
                                    tagColors?.[tag]
                                      ? {
                                          backgroundColor: `${tagColors[tag]}26`,
                                          color: tagColors[tag],
                                        }
                                      : undefined
                                  }
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </RadixTooltip.Content>
                  </RadixTooltip.Portal>
                </RadixTooltip.Root>
              </RadixTooltip.Provider>
            );
          })}

          {filteredLeads.length === 0 ? (
            <div className="col-span-full py-10 text-center text-sm text-muted-foreground">
              {leadSearch || leadTagFilter
                ? 'No leads lists match your filters.'
                : 'No leads lists in this campaign.'}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default LeadsColumn;
